import { useState } from 'react'
import type { StageAllocation } from '../../types/strategy'
import type { PerpPosition } from '../../types/perp'
import { useAuth } from '../../auth/AuthContext'
import { useWalletBalances } from '../../hooks/useWalletBalance'
import { useWalletAdapterBridge } from '../../lib/walletBridge'
import SuggestionCard from '../shared/SuggestionCard'
import HedgeForm from '../trade/HedgeForm'

const JUPITER_PERPS_URL = 'https://jup.ag/perps'

function usd(n: number) {
  return '$' + Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })
}
function sol(n: number) {
  return n.toLocaleString('en-US', { maximumFractionDigits: 3, minimumFractionDigits: 2 }) + ' SOL'
}
function pct(v: number, d = 2) {
  return (v * 100).toFixed(d) + '%'
}
function solQty(p: PerpPosition): number {
  const mp = parseFloat(p.markPrice)
  return mp > 0 ? parseFloat(p.sizeUsd) / mp : 0
}

interface Props {
  positions: PerpPosition[]
  hedgeStage?: StageAllocation
  onClose: () => void
}

export default function JupiterPerpsPanel({ positions, hedgeStage, onClose }: Props) {
  const { portfolioId } = useAuth()
  const balances = useWalletBalances(portfolioId)
  useWalletAdapterBridge()

  // Native in-app form is hidden by default because Jupiter's tx-build API is
  // currently returning 500 for all requests during their Perps migration. Users
  // are routed to jup.ag/perps via the primary CTA. The form is still rendered
  // behind a disclosure for (a) read-only position display and (b) when Jupiter
  // stabilises their endpoint — so we can re-enable by flipping the default.
  const [showNativeForm, setShowNativeForm] = useState(false)

  const totalNotional = positions.reduce((s, p) => s + parseFloat(p.sizeUsd), 0)
  const totalSol = positions.reduce((s, p) => s + solQty(p), 0)

  const hasGap = hedgeStage && hedgeStage.gapUsd != null && Math.abs(hedgeStage.gapUsd) > 1
  const isUnder = hasGap && hedgeStage!.gapUsd! > 0
  const gapAbs = hasGap ? Math.abs(hedgeStage!.gapUsd!) : 0
  const gapPct = hasGap && hedgeStage!.amountUsd > 0
    ? Math.abs(hedgeStage!.gapUsd!) / hedgeStage!.amountUsd
    : 0

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="relative w-full max-w-lg bg-[#131a2a] border-l border-fringe-strong shadow-2xl flex flex-col h-full animate-slide-in-right overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-fringe-strong shrink-0">
          <div className="w-7 h-7 rounded-lg bg-yellow-400/10 flex items-center justify-center text-amber-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <polyline points="2 17 8 11 13 16 22 7" />
              <polyline points="16 7 22 7 22 13" />
            </svg>
          </div>
          <span className="text-sm font-bold text-ink">Jupiter Perps</span>
          {positions.length > 0 && (
            <span className="text-xs text-ink-muted font-medium">
              {positions.length} position{positions.length !== 1 ? 's' : ''} &middot; {totalSol > 0 ? `${sol(totalSol)} (${usd(totalNotional)})` : usd(totalNotional)}
            </span>
          )}
          <button onClick={onClose} className="ml-auto text-gray-500 hover:text-ink transition-colors p-1">
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="3" x2="13" y2="13" /><line x1="13" y1="3" x2="3" y2="13" />
            </svg>
          </button>
        </div>

        {/* Positions summary strip */}
        {positions.length > 0 && (
          <div className="px-4 py-2 border-b border-fringe-strong shrink-0 overflow-x-auto scrollbar-thin scrollbar-thumb-surface">
            <div className="flex gap-2">
              {positions.map((pos, i) => {
                const pnl = parseFloat(pos.unrealizedPnlUsd)
                const qty = solQty(pos)
                return (
                  <div key={pos.provider + pos.market + pos.side + i} className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-surface-hover border border-fringe shrink-0">
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      pos.side === 'long' ? 'bg-accent-green/20 text-accent-green' : 'bg-accent-red/20 text-accent-red'
                    }`}>{pos.side}</span>
                    <span className="text-[10px] text-ink font-medium">{pos.market}</span>
                    {qty > 0 ? (
                      <>
                        <span className="text-[10px] text-ink">{sol(qty)}</span>
                        <span className="text-[10px] text-gray-500">{usd(parseFloat(pos.sizeUsd))}</span>
                      </>
                    ) : (
                      <span className="text-[10px] text-ink-muted">{usd(parseFloat(pos.sizeUsd))}</span>
                    )}
                    {pnl !== 0 && (
                      <span className={`text-[10px] font-medium ${pnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                        {pnl >= 0 ? '+' : '-'}{usd(Math.abs(pnl))}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Gap recommendation */}
        {hasGap && (
          <div className="px-4 py-2 border-b border-fringe-strong shrink-0">
            <SuggestionCard
              action={isUnder ? 'Open short perp hedge' : 'Reduce hedge position'}
              amount={`${isUnder ? '+' : '−'}${usd(gapAbs)}`}
              pctChange={`(${pct(gapPct, 1)})`}
              isPositive={!!isUnder}
              description={isUnder
                ? 'Your portfolio has unhedged directional exposure. Opening a short perp position offsets downside risk from staking and LP positions while preserving yield.'
                : 'Your hedge exceeds the recommended size. Reducing it lowers funding costs and frees margin for other stages.'}
              parameters={[
                ...(hedgeStage!.leverageFactor != null ? [{ label: 'Leverage', value: hedgeStage!.leverageFactor.toFixed(1) + '×' }] : []),
                ...(hedgeStage!.stopLossPrice != null ? [{ label: 'Stop Loss', value: usd(hedgeStage!.stopLossPrice) + ' /SOL', color: 'text-red-400' }] : []),
                ...(hedgeStage!.availableMarginUsd != null ? [{ label: 'Margin', value: usd(hedgeStage!.availableMarginUsd) }] : []),
              ]}
              impacts={[
                { label: 'Funding cost', value: pct(Math.abs(hedgeStage!.yieldOrCostApr)), color: 'text-red-400' },
                { label: 'Daily cost', value: '-' + usd(Math.abs(hedgeStage!.dailyYieldOrCostUsd)), color: 'text-red-400' },
                ...(hedgeStage!.riskLevel ? [{ label: 'Risk', value: hedgeStage!.riskLevel,
                  color: hedgeStage!.riskLevel === 'LOW' ? 'text-accent-green' : hedgeStage!.riskLevel === 'MEDIUM' ? 'text-amber-400' : 'text-red-400' }] : []),
              ]}
            />
          </div>
        )}

        {/* Primary action — opens Jupiter's own Perps UI in a new tab with the
            same wallet. Prominent because Jupiter's tx-build API is returning
            500 for all requests during their Perps migration. */}
        <div className="px-4 py-4 border-b border-fringe-strong shrink-0 bg-amber-400/5 space-y-2.5">
          <div className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2">
            <div className="flex items-start gap-2">
              <svg viewBox="0 0 20 20" className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" fill="currentColor">
                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 4a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 9a1.2 1.2 0 110-2.4 1.2 1.2 0 010 2.4z" />
              </svg>
              <div>
                <p className="text-[11px] font-semibold text-amber-400">Jupiter Perps API is migrating</p>
                <p className="text-[10px] text-ink-muted leading-relaxed mt-0.5">
                  Jupiter's in-app tx-builder is returning 500 for all new shorts.
                  Opening positions from Jupiter's own UI with the same wallet
                  is the reliable path right now.
                </p>
              </div>
            </div>
          </div>
          <a
            href={JUPITER_PERPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="lab-btn w-full justify-center"
          >
            Open Jupiter Perps UI ↗
          </a>
        </div>

        {/* Native Hedge form — hidden by default while Jupiter's API is flaky.
            Still renders position reads via /api/jupiter/perps/positions (backend
            already handles the `walletAddress` rename once deployed). */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <button
            type="button"
            onClick={() => setShowNativeForm(v => !v)}
            className="w-full px-4 py-3 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-surface-hover transition-colors border-b border-fringe"
          >
            <span>Advanced: native in-app form</span>
            <svg viewBox="0 0 16 16" className={`w-3 h-3 transition-transform ${showNativeForm ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>
          {showNativeForm && (
            <div className="p-3">
              <HedgeForm balances={balances} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
